(ns machtfit.health-expense.admin.detail
  (:require [cljs-time.core :refer [today]]
            [machtfit.api :as api]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.datetime :as datetime]
            [machtfit.human :as human]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(defn ^:export load [js-data]
  (rf/dispatch-sync [::init (js->clj js-data :keywordize-keys true)]))

(rf/reg-event-db ::health-expense-updated
  (fn [db [_ health-expense]]
    (-> db
        (assoc-in [:ui :show-accept-modal?] false)
        (assoc-in [:ui :show-reject-modal?] false)
        (update :health-expense #(merge % (select-keys health-expense [:status :reviewer-comments :accepted-amount :clearance-date])))
        (assoc-in [:ui :changed-amount] (-> (or (:accepted-amount health-expense) (:amount health-expense)) human/human->amount human/amountformat))
        (assoc-in [:ui :clearance-date] (some-> (:clearance-date health-expense) datetime/iso-date->date-string))
        (assoc-in [:ui :reviewer-comments] ""))))

(rf/reg-sub ::remaining-balance
  :<- [:get [:ui :changed-amount]]
  :<- [:get [:user-balance]]
  :<- [:get [:health-expense]]

  (fn [[changed-amount balance health-expense] _]
    (let [balance-cents (-> balance human/human->amount human/amount->cents)
          amount-cents (-> changed-amount human/human->amount human/amount->cents)
          accepted-amount-cents (-> (:accepted-amount health-expense) human/human->amount human/amount->cents)
          status (:status health-expense)
          delta-cents (if (= status "accepted") (- amount-cents (or accepted-amount-cents 0)) amount-cents)
          remaining-cents (-> balance-cents (- delta-cents) (max 0))]
      (-> remaining-cents (* 0.01) human/human->amount human/amountformat))))

(rf/reg-event-fx ::api-success
  (fn [{:keys [db]} [_ {result :result error :error success? :success}]]
    {:db (assoc-in db [:ui :in-progress?] false)
     :fx [[:dispatch
           (if success?
             [::health-expense-updated result]
             [:set-path [:ui :error-message] (str "Es ist ein Fehler aufgetreten: " (:name error))])]]}))

(rf/reg-event-fx ::api-error
  (fn [{:keys [db]} _]
    {:db (-> db
             (assoc-in [:ui :in-progress?] false)
             (assoc-in [:ui :error-message] "Es ist ein Fehler aufgetreten."))}))

(rf/reg-event-fx ::accept-health-expense
  (fn [{:keys [db]} _]
    (when-not (get-in db [:ui :in-progress?])
      (let [params {:health_expense_id (-> db :health-expense :id)
                    :amount (-> db :ui :changed-amount human/human->amount human/amount->cents)
                    :reviewer_comments (-> db :ui :reviewer-comments)
                    :clearance_date (-> db :ui :clearance-date datetime/string->date datetime/date->iso-date)
                    :preventive_offer (-> db :health-expense :preventive-offer?)}
            function :health-expense-accept]
        {:db (assoc-in db [:ui :in-progress?] true)
         :post (api/bare-request db
                                 {:data `[(~function ~params :health-expense)]
                                  :on-success #(rf/dispatch [::api-success (-> % :result :health-expense)])
                                  :on-error #(rf/dispatch [::api-error])})}))))

(rf/reg-event-fx ::reject-health-expense
  (fn [{:keys [db]} _]
    (when-not (get-in db [:ui :in-progress?])
      (let [params {:health_expense_id (-> db :health-expense :id)
                    :reviewer_comments (-> db :ui :reviewer-comments)}
            function :health-expense-reject]
        {:db (-> db (assoc-in [:ui :in-progress?] true))
         :post (api/bare-request db
                                 {:data `[(~function ~params :health-expense)]
                                  :on-success #(rf/dispatch [::api-success (-> % :result :health-expense)])
                                  :on-error #(rf/dispatch [::api-error])})}))))

(rf/reg-event-db ::open-accept-modal
  (fn [db _]
    (-> db
        (update :ui dissoc :error-message)
        (assoc-in [:ui :show-reject-modal?] false)
        (assoc-in [:ui :show-accept-modal?] true))))

(rf/reg-event-db ::open-reject-modal
  (fn [db _]
    (-> db
        (update :ui dissoc :error-message)
        (assoc-in [:ui :show-accept-modal?] false)
        (assoc-in [:ui :show-reject-modal?] true))))

(rf/reg-sub ::errors
  :<- [:get [:ui :changed-amount]]
  :<- [:get [:user-balance]]
  :<- [:get [:ui :clearance-date]]
  :<- [:get [:health-expense :submitted-by :health-expenses-without-budget-usage?]]
  :<- [:get [:health-expense]]
  :<- [:get [:ui :error-message]]

  (fn [[changed-amount balance clearance-date-str health-expenses-without-budget-usage? health-expense error-message] _]
    (let [changed-amount-cents (-> changed-amount human/human->amount human/amount->cents)
          balance-cents (-> balance human/human->amount human/amount->cents)
          clearance-date (-> clearance-date-str datetime/string->date)
          accepted-amount-cents (some-> (:accepted-amount health-expense)
                                        human/human->amount
                                        human/amount->cents)
          status (:status health-expense)
          delta-cents (if (= status "accepted")
                        (- changed-amount-cents (or accepted-amount-cents 0))
                        changed-amount-cents)]
      (cond-> []
        (and (> delta-cents balance-cents) (not health-expenses-without-budget-usage?))
        (conj "Der Betrag ist höher als das verfügbare Guthaben.")
        (nil? clearance-date)
        (conj "Bitte geben Sie ein Verrechnungsdatum an.")
        (> clearance-date (today))
        (conj "Das Verrechnungsdatum darf nicht in der Zukunft liegen.")
        (some? error-message)
        (conj error-message)))))

;; Tailwind recipes from the dashboard redesign (dashboard/layout/*.html): the page renders
;; on the Bootstrap-free layout shell, so all styling lives in these utility classes.
(def ^:private button-classes
  ["inline-flex" "items-center" "gap-2" "rounded-lg" "px-4" "py-2.5" "text-sm" "font-semibold"
   "shadow-xs" "transition-colors" "disabled:cursor-not-allowed" "disabled:opacity-60"])

(def ^:private primary-button-classes
  (into button-classes ["bg-brand-500" "text-white" "hover:bg-brand-600"]))

(def ^:private danger-button-classes
  (into button-classes ["bg-error-600" "text-white" "hover:bg-error-700"]))

(def ^:private secondary-button-classes
  (into button-classes ["border" "border-gray-300" "bg-white" "text-gray-700" "hover:bg-gray-50"]))

(def ^:private input-classes
  ["rounded-lg" "border" "border-gray-200" "bg-white" "text-sm" "text-gray-900" "transition-colors"
   "hover:border-gray-300" "focus:border-brand-400" "focus:outline-none" "focus:ring" "focus:ring-brand-500/15"])

(def ^:private link-classes
  ["font-semibold" "text-aquafitblue-700" "hover:text-aquafitblue-800" "hover:underline"])

(def ^:private inline-input-classes
  (into input-classes ["px-2.5" "py-1.5"]))

(defn- error-messages [errors]
  (when (seq errors)
    [:div {:role "alert"
           :class ["mt-4" "rounded-lg" "border" "border-error-200" "bg-error-50" "px-4" "py-3" "text-sm" "text-error-700"]}
     (into [:ul.flex.flex-col.gap-1]
           (for [error errors] [:li error]))]))

(defn- modal-shell [title on-close & body]
  [:div {:class ["fixed" "inset-0" "z-50" "flex" "items-center" "justify-center" "bg-gray-900/40" "p-4"]
         :role "dialog"
         :aria-modal "true"}
   (into [:div {:class ["w-full" "max-w-lg" "rounded-2xl" "bg-white" "p-6" "shadow-lg"]}
          [:div {:class ["mb-5" "flex" "items-start" "justify-between" "gap-4"]}
           [:h3 {:class ["text-lg" "font-bold" "tracking-tight" "text-gray-900"]} title]
           [:button {:type "button"
                     :aria-label "Schließen"
                     :class ["-m-2" "flex" "size-9" "shrink-0" "items-center" "justify-center" "rounded-lg"
                             "text-xl" "text-gray-400" "transition-colors" "hover:bg-gray-50" "hover:text-gray-600"]
                     :on-click on-close}
            "×"]]]
         body)])

(defn- balance-row [label & content]
  (into [:div.flex.items-center.justify-between.gap-4
         [:span.font-semibold.text-gray-700 label]]
        content))

(defn- comment-textarea [label required?]
  [:<>
   [:label {:for "reviewer-comments" :class ["text-sm" "font-semibold" "text-gray-700"]} label]
   [:textarea {:id "reviewer-comments"
               :name "reviewer-comments"
               :maxLength "4096"
               :required required?
               :class (into input-classes ["mt-1.5" "min-h-24" "w-full" "px-3" "py-2"])
               :value @(rf/subscribe [:get [:ui :reviewer-comments]])
               :on-change #(rf/dispatch [:set-path [:ui :reviewer-comments] (-> % .-target .-value)])}]])

(defn- accept-modal []
  (let [errors @(rf/subscribe [::errors])
        in-progress? @(rf/subscribe [:get [:ui :in-progress?]])]
    (when @(rf/subscribe [:get [:ui :show-accept-modal?]])
      [modal-shell "Erstattungsantrag akzeptieren" #(rf/dispatch [:set-path [:ui :show-accept-modal?] false])
       [:div {:class ["mb-5" "flex" "flex-col" "gap-3" "text-sm"]}
        [balance-row "Eingereichter Betrag:"
         [:span.tabular-nums (-> @(rf/subscribe [:get [:health-expense]]) :amount human/human->amount human/amountformat) " €"]]
        [balance-row "Verfügbares Guthaben:"
         [:span.tabular-nums (-> @(rf/subscribe [:get [:user-balance]]) human/human->amount human/amountformat) " €"]]
        [balance-row "Zu verrechnender Betrag:"
         [:span.tabular-nums
          [widgets/inline-input
           {:type widgets/type-amount
            :value @(rf/subscribe [:get [:ui :changed-amount]])
            :class inline-input-classes
            :style {:text-align "right" :width "7rem"}
            :on-save #(rf/dispatch [:set-path [:ui :changed-amount] %])}]
          " €"]]
        [balance-row "Verbleibendes Guthaben:"
         [:span.tabular-nums @(rf/subscribe [::remaining-balance]) " €"]]
        [balance-row "Datum der Verrechnung:"
         [widgets/inline-date-input {:validate-virgin? true
                                     :date-info? false
                                     :class inline-input-classes
                                     :style {:text-align "right" :width "8.5rem"}
                                     :value @(rf/subscribe [:get [:ui :clearance-date]])
                                     :on-save #(rf/dispatch [:set-path [:ui :clearance-date] %])}]]
        [balance-row "zertifiziertes Präventionangebot:"
         [:input {:type "checkbox"
                  :id "preventive-offer-checkbox"
                  :class ["size-[18px]" "cursor-pointer" "accent-brand-500"]
                  :checked @(rf/subscribe [:get [:health-expense :preventive-offer?]])
                  :on-change #(rf/dispatch [:toggle [:health-expense :preventive-offer?]])}]]]

       [comment-textarea "Zusätzliche Bemerkungen" false]
       [error-messages errors]
       [:div {:class ["mt-6" "flex" "justify-end" "gap-2.5"]}
        [:button {:class secondary-button-classes
                  :on-click #(rf/dispatch-sync [:set-path [:ui :show-accept-modal?] false])}
         "Abbrechen"]
        [:button {:class primary-button-classes
                  :disabled in-progress?
                  :on-click #(rf/dispatch-sync [::accept-health-expense])}
         "Akzeptieren"]]])))

(defn- reject-modal []
  (let [in-progress? @(rf/subscribe [:get [:ui :in-progress?]])
        error @(rf/subscribe [:get [:ui :error-message]])]
    (when @(rf/subscribe [:get [:ui :show-reject-modal?]])
      [modal-shell "Erstattungsantrag ablehnen" #(rf/dispatch [:set-path [:ui :show-reject-modal?] false])
       [:p.mb-4.text-sm.text-gray-600 "Der Nutzer wird per E-Mail über die Ablehnung benachrichtigt."]
       [comment-textarea "Grund der Ablehnung" true]
       (when (some? error)
         [error-messages [error]])
       [:div {:class ["mt-6" "flex" "justify-end" "gap-2.5"]}
        [:button {:class secondary-button-classes
                  :on-click #(rf/dispatch-sync [:set-path [:ui :show-reject-modal?] false])}
         "Abbrechen"]
        [:button {:class danger-button-classes
                  :disabled in-progress?
                  :on-click #(rf/dispatch-sync [::reject-health-expense])} "Ablehnen"]]])))

(defn- health-expense-status [status]
  (let [[classes label] (case status
                          "in-review" [["bg-brand-50" "text-brand-700"] "In Prüfung"]
                          "accepted" [["bg-brand-100" "text-brand-700"] "Akzeptiert"]
                          "rejected" [["bg-error-50" "text-error-700"] "Abgelehnt"]
                          nil)]
    (when label
      [:span {:class (into ["inline-flex" "w-fit" "items-center" "whitespace-nowrap" "rounded-full"
                            "px-2.5" "py-0.5" "text-xs" "font-semibold"]
                           classes)}
       label])))

(defn- actions []
  (let [status (:status @(rf/subscribe [:get [:health-expense]]))]
    [:div {:class ["mt-5" "flex" "flex-wrap" "gap-2.5"]}
     (case status
       "accepted"
       [:button {:type "button"
                 :on-click #(rf/dispatch [::open-accept-modal])
                 :class primary-button-classes}
        "Akzeptierten Betrag anpassen"]

       "in-review"
       [:<>
        [:button {:type "button"
                  :on-click #(rf/dispatch [::open-accept-modal])
                  :class primary-button-classes}
         "Akzeptieren"]
        [:button {:type "button"
                  :on-click #(rf/dispatch [::open-reject-modal])
                  :class danger-button-classes}
         "Ablehnen"]]

       [:button {:type "button"
                 :on-click #(rf/dispatch [::open-accept-modal])
                 :class primary-button-classes}
        "Akzeptieren"])]))

(defn- detail-row [label & content]
  [:tr
   [:th {:class ["w-60" "px-5" "py-3" "text-left" "align-top" "font-semibold" "text-gray-500"]} label]
   (into [:td {:class ["px-5" "py-3" "text-gray-900"]}] content)])

(defn health-expense-detail []
  (let [health-expense @(rf/subscribe [:get [:health-expense]])
        reviewer-comments (:reviewer-comments health-expense)
        email (-> health-expense :submitted-by :email)
        accepted-amount (:accepted-amount health-expense)
        iban-required? @(rf/subscribe [:get [:iban-required?]])
        other-health-expenses @(rf/subscribe [:get [:other-health-expenses]])
        reviewer-note @(rf/subscribe [:get [:reviewer-note]])]
    [:div
     [accept-modal]
     [reject-modal]
     [:div {:class ["grid" "items-start" "gap-5" "lg:grid-cols-2"]}
      [:div
       [:div {:class ["overflow-hidden" "rounded-2xl" "border" "border-gray-100" "bg-white" "shadow-xs"]}
        [:table.w-full.text-sm
         [:tbody.divide-y.divide-gray-100
          [detail-row "Status"
           [health-expense-status (:status health-expense)]]
          [detail-row "Datum des Erstattungsantrags"
           (-> health-expense :submitted-at datetime/iso-datetime->datetime-string)]
          (when (-> health-expense :accepted-at some?)
            [detail-row "Akzeptiert"
             (-> health-expense :accepted-at datetime/iso-datetime->datetime-string)
             (when (-> health-expense :accepted-by some?)
               (str " von " (-> health-expense :accepted-by :name)))])
          (when (-> health-expense :clearance-date some?)
            [detail-row "Datum der Verrechnung"
             (-> health-expense :clearance-date datetime/iso-date->date-string)])
          [detail-row "Mitarbeiter*in"
           [:a {:href (-> health-expense :submitted-by :employee-url)
                :class link-classes}
            (-> health-expense :submitted-by :name)]
           [:br]
           [:a {:href (str "mailto:" email) :class link-classes} email]]
          [detail-row "Unternehmen"
           (-> health-expense :submitted-by :company-name)]
          [detail-row "zertifiziertes Präventionsangebot"
           (if (:preventive-offer? health-expense) "Ja" "Nein")]
          [detail-row "Eingereichte Nachweise"
           (into [:ul.flex.flex-col.gap-1]
                 (for [file (:files health-expense)]
                   [:li {:key (:id file)}
                    [:a {:href (:url file) :target "_blank" :class link-classes} (:name file)]]))]
          [detail-row "Betrag"
           (-> health-expense :amount human/human->amount human/amountformat)
           " €"]
          (when accepted-amount
            [detail-row "Akzeptierter Betrag"
             (-> accepted-amount human/human->amount human/amountformat)
             " €"])
          [detail-row "Aktivität" (:activity health-expense)]
          [detail-row "Kategorie" (:category health-expense)]
          [detail-row "Zeitraum"
           (-> health-expense :invoice-period-start datetime/iso-date->date human/dateformat-long)
           " – "
           (-> health-expense :invoice-period-end datetime/iso-date->date human/dateformat-long)]
          (when iban-required?
            [detail-row "IBAN" (:extra-field health-expense)])
          [detail-row "Bemerkungen des Einreichenden" (:submitter-comments health-expense)]
          (when (seq reviewer-comments)
            [detail-row "Prüfungskommentar" reviewer-comments])]]]
       [actions]
       [:div.mt-8
        [:h2 {:class ["mb-3" "text-lg" "font-bold" "tracking-tight" "text-gray-900"]} "Weitere Einreichungen"]
        (if (seq other-health-expenses)
          (into [:ul {:class ["flex" "flex-col" "gap-1.5" "text-sm" "text-gray-600"]}]
                (for [other-health-expense other-health-expenses]
                  [:li {:key (:id other-health-expense)
                        :class ["flex" "flex-wrap" "items-center" "gap-1.5"]}
                   [:a {:href (:url other-health-expense) :target "_blank" :class link-classes}
                    (str "Erstattungsantrag #" (:id other-health-expense))]
                   [health-expense-status (:status other-health-expense)]
                   (str "eingereicht am " (-> other-health-expense :submitted-at datetime/iso-datetime->datetime-string))]))
          [:p.text-sm.text-gray-600 "Keine weiteren Einreichungen für diesen Nutzenden gefunden."])]]
      (when (seq reviewer-note)
        [:div {:class ["rounded-2xl" "border" "border-gray-100" "bg-white" "p-6" "shadow-xs"]}
         [:div {:class ["prose" "prose-sm" "max-w-none"]
                :dangerouslySetInnerHTML (r/unsafe-html reviewer-note)}]])]]))

(rf/reg-event-db ::init
  (fn [db [_ js-data]]
    (-> db
        (merge (select-keys js-data [:csrf-token :reviewer-note :iban-required? :user-balance :health-expense :other-health-expenses]))
        (assoc-in [:ui :changed-amount]
                  (-> js-data :health-expense ((fn [health-expense] (or (:accepted-amount health-expense) (:amount health-expense)))) human/human->amount human/amountformat))
        (assoc-in [:ui :reviewer-comments] "")
        (assoc-in
         [:ui :clearance-date]
         (if-let [existing-clearance-date (-> js-data :health-expense :clearance-date)]
           ;; convert stored ISO date to the display format expected by the date widget
           (-> existing-clearance-date datetime/iso-date->date-string)
           ;; default: today in display format
           (-> (today)
               datetime/date->iso-date
               datetime/iso-date->date-string))))))
